import { useState, useCallback, useRef, useEffect } from 'react';
import ProjectDisplayController from '../controllers/ProjectDisplayController';
import MapLogger from '../utils/MapLogger';

/**
 * Hook for managing project display on map
 * Provides simplified interface for controlling residential and land project display
 */
export const useProjectDisplay = (mapRef, onParcelClick = null) => {
  const [currentProject, setCurrentProject] = useState(null);
  const [currentProjectType, setCurrentProjectType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [currentFloorFilters, setCurrentFloorFilters] = useState([]);

  // Controller reference
  const controllerRef = useRef(null);

  // Initialize controller
  useEffect(() => {
    if (mapRef && mapRef.current && !controllerRef.current) {
      controllerRef.current = new ProjectDisplayController(mapRef, onParcelClick);
      MapLogger.logMapInfo('Project display controller initialized');
    }
  }, [mapRef, onParcelClick]);

  // Initialize controller when map becomes available
  useEffect(() => {
    const checkMapReady = () => {
      if (mapRef && mapRef.current && !controllerRef.current) {
        controllerRef.current = new ProjectDisplayController(mapRef, onParcelClick);
        MapLogger.logMapInfo('Project display controller initialized (delayed)');
      }
    };

    // Check immediately
    checkMapReady();

    // Also check periodically until map is ready
    const interval = setInterval(() => {
      if (mapRef && mapRef.current && !controllerRef.current) {
        checkMapReady();
        clearInterval(interval);
      }
    }, 100);

    // Cleanup after 5 seconds
    setTimeout(() => clearInterval(interval), 5000);

    return () => clearInterval(interval);
  }, [mapRef]);

  // Update map reference in controller
  useEffect(() => {
    if (controllerRef.current && mapRef) {
      controllerRef.current.updateMapRef(mapRef);
    }
  }, [mapRef]);

  /**
   * Display residential project
   * @param {number} projectId - Project ID
   * @param {Array} floorFilters - Floor filters
   * @param {Object} project - Project data
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} - Display result
   */
  const displayResidentialProject = useCallback(async (projectId, floorFilters, project, options = {}) => {
    // Check if same project is already being displayed with same floor filters
    if (currentProject === projectId && currentProjectType === 'residential' && 
        JSON.stringify(currentFloorFilters) === JSON.stringify(floorFilters) && !isLoading) {
      MapLogger.logMapInfo('Same residential project with same floor filters already displayed, skipping', { 
        projectId, 
        floorFilters 
      });
      return { success: true, message: 'Project already displayed' };
    }

    // Wait for controller to be initialized
    if (!controllerRef.current) {
      // Try to initialize if map is ready
      if (mapRef && mapRef.current) {
        controllerRef.current = new ProjectDisplayController(mapRef, onParcelClick);
        MapLogger.logMapInfo('Controller initialized on demand');
      } else {
        const error = 'Project display controller not initialized and map not ready';
        setLastError(error);
        return { success: false, error };
      }
    }

    // Prevent multiple simultaneous loads
    if (isLoading) {
      MapLogger.logMapInfo('Project display already in progress, skipping', { projectId });
      return { success: false, error: 'Display already in progress' };
    }

    setIsLoading(true);
    setLastError(null);

    try {
      const result = await controllerRef.current.displayResidentialProject(
        projectId, 
        floorFilters, 
        project, 
        options
      );

      if (result.success) {
        setCurrentProject(projectId);
        setCurrentProjectType('residential');
        setCurrentFloorFilters(floorFilters);
      } else {
        setLastError(result.error);
      }

      return result;
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Display land project
   * @param {number} projectId - Project ID
   * @param {Object} project - Project data
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} - Display result
   */
  const displayLandProject = useCallback(async (projectId, project, options = {}) => {
    // Check if same project is already being displayed
    if (currentProject === projectId && currentProjectType === 'land' && !isLoading) {
      MapLogger.logMapInfo('Same land project already displayed, skipping', { projectId });
      return { success: true, message: 'Project already displayed' };
    }

    // Wait for controller to be initialized
    if (!controllerRef.current) {
      // Try to initialize if map is ready
      if (mapRef && mapRef.current) {
        controllerRef.current = new ProjectDisplayController(mapRef, onParcelClick);
        MapLogger.logMapInfo('Controller initialized on demand');
      } else {
        const error = 'Project display controller not initialized and map not ready';
        setLastError(error);
        return { success: false, error };
      }
    }

    // Prevent multiple simultaneous loads
    if (isLoading) {
      MapLogger.logMapInfo('Project display already in progress, skipping', { projectId });
      return { success: false, error: 'Display already in progress' };
    }

    setIsLoading(true);
    setLastError(null);

    try {
      const result = await controllerRef.current.displayLandProject(
        projectId, 
        project, 
        options
      );

      if (result.success) {
        setCurrentProject(projectId);
        setCurrentProjectType('land');
        setCurrentFloorFilters([]);
      } else {
        setLastError(result.error);
      }

      return result;
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Apply filters to current project
   * @param {Object} filters - Applied filters
   * @returns {Promise<Object>} - Filter application result
   */
  const applyFilters = useCallback(async (filters) => {
    // Wait for controller to be initialized
    if (!controllerRef.current) {
      // Try to initialize if map is ready
      if (mapRef && mapRef.current) {
        controllerRef.current = new ProjectDisplayController(mapRef, onParcelClick);
        MapLogger.logMapInfo('Controller initialized on demand');
      } else {
        const error = 'Project display controller not initialized and map not ready';
        setLastError(error);
        return { success: false, error };
      }
    }

    try {
      // Pass current floor filters to maintain them during zoom/pan operations
      const result = await controllerRef.current.applyFilters(filters, currentFloorFilters);

      if (!result.success) {
        setLastError(result.error);
      }

      return result;
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [currentFloorFilters]);

  /**
   * Update floor filters for residential projects
   * @param {Array} newFloorFilters - New floor filters
   * @returns {Promise<Object>} - Update result
   */
  const updateFloorFilters = useCallback(async (newFloorFilters) => {
    // Wait for controller to be initialized
    if (!controllerRef.current) {
      // Try to initialize if map is ready
      if (mapRef && mapRef.current) {
        controllerRef.current = new ProjectDisplayController(mapRef, onParcelClick);
        MapLogger.logMapInfo('Controller initialized on demand');
      } else {
        const error = 'Project display controller not initialized and map not ready';
        setLastError(error);
        return { success: false, error };
      }
    }

    setIsLoading(true);
    setLastError(null);

    try {
      const result = await controllerRef.current.updateFloorFilters(newFloorFilters);
      
      if (result.success) {
        setCurrentFloorFilters(newFloorFilters);
      } else {
        setLastError(result.error);
      }

      return result;
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear current project
   * @returns {Promise<Object>} - Clear result
   */
  const clearCurrentProject = useCallback(async () => {
    // Wait for controller to be initialized
    if (!controllerRef.current) {
      // Try to initialize if map is ready
      if (mapRef && mapRef.current) {
        controllerRef.current = new ProjectDisplayController(mapRef, onParcelClick);
        MapLogger.logMapInfo('Controller initialized on demand');
      } else {
        const error = 'Project display controller not initialized and map not ready';
        setLastError(error);
        return { success: false, error };
      }
    }

    try {
      const result = await controllerRef.current.clearCurrentProject();
      
      if (result.success) {
        setCurrentProject(null);
        setCurrentProjectType(null);
        setCurrentFloorFilters([]);
        setLastError(null);
      } else {
        setLastError(result.error);
      }

      return result;
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  /**
   * Reload current project
   * @returns {Promise<Object>} - Reload result
   */
  const reloadCurrentProject = useCallback(async () => {
    if (!controllerRef.current) {
      const error = 'Project display controller not initialized';
      setLastError(error);
      return { success: false, error };
    }

    setIsLoading(true);
    setLastError(null);

    try {
      const result = await controllerRef.current.reloadCurrentProject();
      
      if (!result.success) {
        setLastError(result.error);
      }

      return result;
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get current project information
   * @returns {Object} - Current project information
   */
  const getCurrentProjectInfo = useCallback(() => {
    if (!controllerRef.current) {
      return {
        projectId: null,
        projectType: null,
        floorFilters: [],
        isLoading: false,
        lastError: 'Controller not initialized'
      };
    }

    return controllerRef.current.getCurrentProjectInfo();
  }, []);

  /**
   * Check if project is displayed
   * @returns {boolean} - Project display status
   */
  const hasCurrentProject = useCallback(() => {
    return controllerRef.current ? controllerRef.current.hasCurrentProject() : false;
  }, []);

  /**
   * Check current project type
   * @param {string} projectType - Required project type
   * @returns {boolean} - Project type match
   */
  const isCurrentProjectType = useCallback((projectType) => {
    return controllerRef.current ? controllerRef.current.isCurrentProjectType(projectType) : false;
  }, []);

  /**
   * Clear last error
   */
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.reset();
    }
    setCurrentProject(null);
    setCurrentProjectType(null);
    setCurrentFloorFilters([]);
    setIsLoading(false);
    setLastError(null);
    MapLogger.logMapInfo('Project display hook reset');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.reset();
      }
    };
  }, []);

  return {
    // State
    currentProject,
    currentProjectType,
    isLoading,
    lastError,
    currentFloorFilters,
    
    // Functions
    displayResidentialProject,
    displayLandProject,
    applyFilters,
    updateFloorFilters,
    clearCurrentProject,
    reloadCurrentProject,
    getCurrentProjectInfo,
    hasCurrentProject,
    isCurrentProjectType,
    clearError,
    reset
  };
};

export default useProjectDisplay;
